import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BoardParticipants, type BoardParticipant } from "./BoardParticipants";
import { useBoardAccess } from "../../hooks/useBoardAccess";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";

type Tab = "online" | "members" | "links";

type Props = {
  boardId: string;
  sessionAccess: boolean;
  participants: BoardParticipant[];
  wsHint?: string | null;
  followingPeerId?: string | null;
  onFollow?: (peerId: string) => void;
  onUnfollow?: () => void;
  /** Управляемое открытие панели (кнопка «Настройки» в шапке). */
  panelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
};

function roleLabel(role: string): string {
  return role === "editor" ? "Редактор" : "Только просмотр";
}

export function BoardCollaborationBar({
  boardId,
  sessionAccess,
  participants,
  wsHint,
  followingPeerId,
  onFollow,
  onUnfollow,
  panelOpen,
  onPanelOpenChange,
}: Props) {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = panelOpen !== undefined;
  const open = controlled ? Boolean(panelOpen) : uncontrolledOpen;
  const setOpen = (next: boolean | ((prev: boolean) => boolean)) => {
    const value = typeof next === "function" ? next(open) : next;
    if (!controlled) setUncontrolledOpen(value);
    onPanelOpenChange?.(value);
  };
  const [tab, setTab] = useState<Tab>("members");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRole, setMemberRole] = useState("editor");
  const [linkRole, setLinkRole] = useState("editor");
  const [linkMax, setLinkMax] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [activeLinksOnly, setActiveLinksOnly] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const newLinkInputRef = useRef<HTMLInputElement>(null);
  const linkCopy = useCopyToClipboard();

  const access = useBoardAccess(boardId, sessionAccess);

  useEffect(() => {
    linkCopy.reset();
  }, [newLinkUrl, linkCopy.reset]);

  useEffect(() => {
    if (!open) return;
    function onDoc(ev: PointerEvent) {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onDoc, true);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    void access.reload();
  }, [open, access.reload]);

  const canOpenPanel = sessionAccess && (access.isOwner || access.isMember);
  const visibleLinks = activeLinksOnly
    ? access.links.filter((l) => !l.revoked_at)
    : access.links;

  async function onInviteSubmit(e: FormEvent) {
    e.preventDefault();
    if (!memberEmail.trim()) return;
    setSubmitting(true);
    access.setErr("");
    try {
      await access.addMember(memberEmail, memberRole);
      setMemberEmail("");
    } catch (ex) {
      access.setErr(ex instanceof Error ? ex.message : "Ошибка");
    } finally {
      setSubmitting(false);
    }
  }

  async function onCreateLink(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    access.setErr("");
    try {
      const max = linkMax.trim() ? Number(linkMax) : undefined;
      const url = await access.createShareLink(linkRole, max);
      if (url) setNewLinkUrl(url);
      setLinkMax("");
    } catch (ex) {
      access.setErr(ex instanceof Error ? ex.message : "Ошибка");
    } finally {
      setSubmitting(false);
    }
  }

  if (!participants.length && !wsHint && !canOpenPanel && !open) return null;

  return (
    <div className="wb-board-collab" ref={rootRef}>
      <BoardParticipants
        participants={participants}
        wsHint={wsHint}
        followingPeerId={followingPeerId}
        onFollow={onFollow}
        onUnfollow={onUnfollow}
      />

      {canOpenPanel ? (
        <button
          type="button"
          className={`wb-board-collab__toggle${open ? " is-open" : ""}`}
          aria-expanded={open}
          aria-haspopup="dialog"
          title={access.isOwner ? "Пригласить и управлять доступом" : "Участники доски"}
          onClick={() => {
            setOpen((v) => !v);
            if (!open) setTab("members");
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.8" />
            <path
              d="M19 8v6M22 11h-6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
          <span className="wb-board-collab__toggle-label">
            {access.isOwner ? "Доступ" : "Участники"}
          </span>
        </button>
      ) : null}

      {open && (canOpenPanel || sessionAccess) ? (
        <div className="wb-board-access-panel" role="dialog" aria-label="Настройки доступа">
          <div className="wb-board-access-panel__head">
            <h2 className="wb-board-access-panel__title">Настройки доступа</h2>
            <button
              type="button"
              className="wb-board-access-panel__close"
              aria-label="Закрыть"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="wb-board-access-panel__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "online"}
              className={tab === "online" ? "is-active" : ""}
              onClick={() => setTab("online")}
            >
              Онлайн ({participants.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "members"}
              className={tab === "members" ? "is-active" : ""}
              onClick={() => setTab("members")}
            >
              Участники
            </button>
            {access.isOwner ? (
              <button
                type="button"
                role="tab"
                aria-selected={tab === "links"}
                className={tab === "links" ? "is-active" : ""}
                onClick={() => setTab("links")}
              >
                Ссылки
              </button>
            ) : null}
          </div>

          {access.ok ? (
            <p className="wb-board-access-panel__toast wb-board-access-panel__toast--ok" role="status">
              {access.ok}
            </p>
          ) : null}
          {access.err ? (
            <p className="wb-board-access-panel__toast wb-board-access-panel__toast--err" role="alert">
              {access.err}
            </p>
          ) : null}

          <div className="wb-board-access-panel__body">
            {access.loading && tab !== "online" ? (
              <p className="wb-board-access-panel__muted">Загрузка…</p>
            ) : null}

            {tab === "online" ? (
              <div className="wb-board-access-panel__section">
                <p className="wb-board-access-panel__hint">
                  Сейчас на доске по WebSocket. Клик по имени — следовать за viewport.
                </p>
                {participants.length === 0 ? (
                  <p className="wb-board-access-panel__muted">Пока только вы.</p>
                ) : (
                  <ul className="wb-board-access-panel__roster">
                    {participants.map((p) => (
                      <li key={p.peerId}>
                        {p.displayLabel}
                        {p.isSelf ? " (вы)" : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}

            {tab === "members" ? (
              <div className="wb-board-access-panel__section">
                <ul className="wb-board-access-panel__list">
                  <li className="wb-board-access-panel__row">
                    <span className="wb-board-access-panel__email">{access.ownerEmail}</span>
                    <span className="wb-board-access-panel__role-badge">Владелец</span>
                  </li>
                  {access.members.map((m) => (
                    <li key={m.user_id} className="wb-board-access-panel__row">
                      <span className="wb-board-access-panel__email">{m.email}</span>
                      {access.isOwner ? (
                        <div className="wb-board-access-panel__row-actions">
                          <select
                            className="wb-board-access-panel__select"
                            value={m.role}
                            aria-label={`Роль ${m.email}`}
                            onChange={(e) => {
                              void access.updateMemberRole(m.user_id, e.target.value).catch((ex) =>
                                access.setErr(ex instanceof Error ? ex.message : "Ошибка")
                              );
                            }}
                          >
                            <option value="editor">Редактор</option>
                            <option value="viewer">Просмотр</option>
                          </select>
                          <button
                            type="button"
                            className="wb-board-access-panel__link-danger"
                            onClick={() => {
                              if (!confirm(`Удалить ${m.email} с доски?`)) return;
                              void access.removeMember(m.user_id).catch((ex) =>
                                access.setErr(ex instanceof Error ? ex.message : "Ошибка")
                              );
                            }}
                          >
                            Удалить
                          </button>
                        </div>
                      ) : (
                        <span className="wb-board-access-panel__role-badge">
                          {roleLabel(m.role)}
                        </span>
                      )}
                    </li>
                  ))}
                  {access.pendingInvitations.map((inv) => (
                    <li key={inv.id} className="wb-board-access-panel__row is-pending">
                      <span className="wb-board-access-panel__email">
                        {inv.email}
                        <span className="wb-board-access-panel__pending"> · ожидает</span>
                      </span>
                      <div className="wb-board-access-panel__row-actions">
                        <span className="wb-board-access-panel__role-badge">
                          {roleLabel(inv.role)}
                        </span>
                        {access.isOwner ? (
                          <button
                            type="button"
                            className="wb-board-access-panel__link-danger"
                            onClick={() => {
                              void access.revokeInvitation(inv.id).catch((ex) =>
                                access.setErr(ex instanceof Error ? ex.message : "Ошибка")
                              );
                            }}
                          >
                            Отозвать
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>

                {access.isOwner ? (
                  <form className="wb-board-access-panel__form" onSubmit={onInviteSubmit}>
                    <p className="wb-board-access-panel__form-title">Пригласить по email</p>
                    <input
                      className="wb-board-access-panel__input"
                      type="email"
                      required
                      placeholder="email@example.com"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      disabled={submitting}
                    />
                    <select
                      className="wb-board-access-panel__select wb-board-access-panel__select--block"
                      value={memberRole}
                      onChange={(e) => setMemberRole(e.target.value)}
                      disabled={submitting}
                    >
                      <option value="editor">Редактор</option>
                      <option value="viewer">Только просмотр</option>
                    </select>
                    <button
                      type="submit"
                      className="wb-board-access-panel__btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? "Отправка…" : "Пригласить"}
                    </button>
                  </form>
                ) : null}

                {access.isMember && !access.isOwner ? (
                  <div className="wb-board-access-panel__leave">
                    <button
                      type="button"
                      className="wb-board-access-panel__link-danger"
                      onClick={() => {
                        if (!confirm("Покинуть доску? Она исчезнет из вашего кабинета.")) return;
                        void access
                          .leaveBoard()
                          .then(() => navigate("/cabinet", { replace: true }))
                          .catch((ex) =>
                            access.setErr(ex instanceof Error ? ex.message : "Ошибка")
                          );
                      }}
                    >
                      Покинуть доску
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {tab === "links" && access.isOwner ? (
              <div className="wb-board-access-panel__section">
                <p className="wb-board-access-panel__hint">
                  Ссылка для гостей без аккаунта. Редактор может рисовать; просмотр — только
                  смотреть.
                </p>
                <label className="wb-board-access-panel__label" htmlFor="guest-policy">
                  Гости по ссылке
                </label>
                <select
                  id="guest-policy"
                  className="wb-board-access-panel__select wb-board-access-panel__select--block"
                  value={access.guestPolicy}
                  onChange={(e) => {
                    void access.saveGuestPolicy(e.target.value).catch((ex) =>
                      access.setErr(ex instanceof Error ? ex.message : "Ошибка")
                    );
                  }}
                >
                  <option value="private">Токен в URL (?t=)</option>
                  <option value="link_read">Cookie-сессия (без ?t=)</option>
                </select>

                <label className="wb-board-access-panel__check">
                  <input
                    type="checkbox"
                    checked={activeLinksOnly}
                    onChange={(e) => setActiveLinksOnly(e.target.checked)}
                  />
                  Только активные
                </label>

                <ul className="wb-board-access-panel__list">
                  {visibleLinks.length === 0 ? (
                    <li className="wb-board-access-panel__muted">Активных ссылок нет.</li>
                  ) : null}
                  {visibleLinks.map((l) => (
                    <li key={l.id} className="wb-board-access-panel__row">
                      <span className="wb-board-access-panel__email">
                        {roleLabel(l.role)} · {l.uses_count}
                        {l.max_uses != null ? ` / ${l.max_uses}` : " исп."}
                      </span>
                      {!l.revoked_at ? (
                        <button
                          type="button"
                          className="wb-board-access-panel__link-danger"
                          onClick={() => {
                            if (!confirm("Отозвать ссылку?")) return;
                            void access.revokeShareLink(l.id).catch((ex) =>
                              access.setErr(ex instanceof Error ? ex.message : "Ошибка")
                            );
                          }}
                        >
                          Отозвать
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>

                <form className="wb-board-access-panel__form" onSubmit={onCreateLink}>
                  <p className="wb-board-access-panel__form-title">Новая ссылка</p>
                  <select
                    className="wb-board-access-panel__select wb-board-access-panel__select--block"
                    value={linkRole}
                    onChange={(e) => setLinkRole(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="editor">Редактор (без аккаунта)</option>
                    <option value="viewer">Только просмотр</option>
                  </select>
                  <input
                    className="wb-board-access-panel__input"
                    type="number"
                    min={1}
                    placeholder="Лимит использований (необяз.)"
                    value={linkMax}
                    onChange={(e) => setLinkMax(e.target.value)}
                    disabled={submitting}
                  />
                  <button
                    type="submit"
                    className="wb-board-access-panel__btn-primary"
                    disabled={submitting}
                  >
                    Создать ссылку
                  </button>
                </form>

                {newLinkUrl ? (
                  <div className="wb-board-access-panel__new-link">
                    <p className="wb-board-access-panel__label">Скопируйте ссылку (токен больше не покажем):</p>
                    <input
                      ref={newLinkInputRef}
                      className="wb-board-access-panel__input"
                      readOnly
                      value={newLinkUrl}
                    />
                    <button
                      type="button"
                      className={`wb-board-access-panel__btn-ghost${linkCopy.copied ? " is-copied" : ""}${linkCopy.error ? " is-error" : ""}`}
                      onClick={() => void linkCopy.copy(newLinkUrl, newLinkInputRef.current)}
                    >
                      {linkCopy.buttonLabel}
                    </button>
                    {linkCopy.copied ? (
                      <p
                        className="wb-board-access-panel__toast wb-board-access-panel__toast--ok"
                        role="status"
                      >
                        Ссылка скопирована в буфер обмена
                      </p>
                    ) : null}
                    {linkCopy.error ? (
                      <p
                        className="wb-board-access-panel__toast wb-board-access-panel__toast--err"
                        role="alert"
                      >
                        Не удалось скопировать — выделите ссылку и нажмите Ctrl+C
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
